import { useEffect, useMemo, useState } from 'react';
import Sheet from '@/components/ui/Sheet';
import type { OpcaoFiltro } from '../types';

interface SeletorFiltroProps {
  rotulo: string;
  icone: string;
  opcoes: OpcaoFiltro[];
  selecionados: string[];
  onChange: (valores: string[]) => void;
  placeholderBusca?: string;
}

// Descobre se a tela é de computador (a partir de 768 px). No computador o
// seletor abre como uma lista logo abaixo do botão; no celular, como folha.
function useEhDesktop(): boolean {
  const [desktop, setDesktop] = useState(() =>
    typeof window === 'undefined' ? true : window.matchMedia('(min-width: 768px)').matches,
  );
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 768px)');
    const aoMudar = () => setDesktop(mq.matches);
    mq.addEventListener('change', aoMudar);
    return () => mq.removeEventListener('change', aoMudar);
  }, []);
  return desktop;
}

function semAcento(s: string): string {
  return s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
}

// Lista de opções com busca e marcação múltipla (o conteúdo compartilhado entre
// a folha do celular e a lista do computador).
function ConteudoOpcoes({
  opcoes,
  selecionados,
  aoAlternar,
  placeholderBusca,
}: {
  opcoes: OpcaoFiltro[];
  selecionados: string[];
  aoAlternar: (valor: string) => void;
  placeholderBusca?: string;
}) {
  const [busca, setBusca] = useState('');

  const filtradas = useMemo(() => {
    const termo = semAcento(busca.trim());
    if (!termo) return opcoes;
    return opcoes.filter((o) => semAcento(o.rotulo).includes(termo));
  }, [busca, opcoes]);

  return (
    <div>
      <div className="relative">
        <span className="pointer-events-none absolute left-3 top-1/2 flex h-4 w-4 -translate-y-1/2 items-center justify-center text-foreground-400">
          <i className="ri-search-line" aria-hidden />
        </span>
        <input
          type="text"
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
          placeholder={placeholderBusca ?? 'Buscar…'}
          className="h-11 w-full rounded-xl border border-secondary-300 bg-white pl-9 pr-3 text-sm text-foreground-950 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-400"
        />
      </div>

      <div className="mt-2 max-h-64 overflow-y-auto">
        {filtradas.length === 0 ? (
          <p className="px-1 py-6 text-center text-sm text-foreground-500">Nada encontrado.</p>
        ) : (
          <ul className="space-y-1">
            {filtradas.map((o) => {
              const marcado = selecionados.includes(o.valor);
              return (
                <li key={o.valor}>
                  <button
                    type="button"
                    onClick={() => aoAlternar(o.valor)}
                    className={`aperta flex w-full cursor-pointer items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-colors ${
                      marcado ? 'bg-primary-50' : 'hover:bg-background-100'
                    }`}
                  >
                    <span
                      className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-md border ${
                        marcado
                          ? 'border-primary-500 bg-primary-500 text-white'
                          : 'border-secondary-300 bg-white text-transparent'
                      }`}
                    >
                      <i className="ri-check-line text-sm" aria-hidden />
                    </span>
                    <span className="min-w-0 flex-1 truncate text-sm font-medium text-foreground-950">
                      {o.rotulo}
                    </span>
                    <span className="shrink-0 text-xs text-foreground-400">{o.quantidade}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

// Seletor de múltipla escolha com busca. No computador abre como lista abaixo
// do botão; no celular, como folha que sobe.
export default function SeletorFiltro({
  rotulo,
  icone,
  opcoes,
  selecionados,
  onChange,
  placeholderBusca,
}: SeletorFiltroProps) {
  const ehDesktop = useEhDesktop();
  const [aberto, setAberto] = useState(false);

  const alternar = (valor: string) => {
    if (selecionados.includes(valor)) {
      onChange(selecionados.filter((v) => v !== valor));
    } else {
      onChange([...selecionados, valor]);
    }
  };

  const tem = selecionados.length > 0;

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setAberto((v) => !v)}
        className={`aperta flex h-12 w-full cursor-pointer items-center gap-2 rounded-xl border px-3 text-left transition-colors ${
          tem ? 'border-primary-300 bg-primary-50' : 'border-secondary-300 bg-white'
        }`}
      >
        <span
          className={`flex h-4 w-4 shrink-0 items-center justify-center ${tem ? 'text-primary-600' : 'text-foreground-400'}`}
        >
          <i className={icone} aria-hidden />
        </span>
        <span className="min-w-0 flex-1 truncate text-sm font-medium text-foreground-900">
          {rotulo}
        </span>
        {tem && (
          <span className="flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full bg-primary-500 px-1.5 text-xs font-bold text-white">
            {selecionados.length}
          </span>
        )}
        <span
          className={`flex h-4 w-4 shrink-0 items-center justify-center text-foreground-400 transition-transform ${aberto ? 'rotate-180' : ''}`}
        >
          <i className="ri-arrow-down-s-line" aria-hidden />
        </span>
      </button>

      {ehDesktop && aberto && (
        <>
          <div className="fixed inset-0 z-20" onClick={() => setAberto(false)} aria-hidden />
          <div className="absolute left-0 right-0 z-30 mt-2 rounded-[16px] border border-background-200 bg-white p-3 shadow-lg">
            <ConteudoOpcoes
              opcoes={opcoes}
              selecionados={selecionados}
              aoAlternar={alternar}
              placeholderBusca={placeholderBusca}
            />
          </div>
        </>
      )}

      {!ehDesktop && (
        <Sheet aberto={aberto} titulo={rotulo} aoFechar={() => setAberto(false)}>
          <ConteudoOpcoes
            opcoes={opcoes}
            selecionados={selecionados}
            aoAlternar={alternar}
            placeholderBusca={placeholderBusca}
          />
        </Sheet>
      )}
    </div>
  );
}